/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, useContext } from 'react';
import Api from '../../apis/projectApi';
import { Project } from '../../Models/Project';
import MikeTable from '../../components/mike-table';
import { GlobalContext } from '../../store/Store';
import  ActionType from '../../store/ActionType';
import ProjectCreateForm from './ProjectCreateForm';
import MikeSlidingPanel from '../../components/mike-sliding-panel';
import Projects from '../../icons/Projects.svg?react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';

export const columns = [
  {
    id: 'name',
    type: 'link',
    label: 'Project name',
    sortable: true,
    render(_row: any, value: React.ReactNode) {
      return (
        <>
          <span>
            <Projects />
          </span>
          <span
            style={{
              cursor: 'pointer',
              display: 'block',
              marginTop: '12px',
              maxWidth: '80%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            <Tooltip title={value}>
              <>{value}</>
            </Tooltip>
          </span>
        </>
      );
    },
    filtrable: true,
  },
  {
    id: 'description',
    label: 'Description',
    sortable: true,
    render(_row: any, value: React.ReactNode) {
      return (
        <div
          style={{
            display: 'table-cell',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '90%',
          }}>
          <Tooltip title={value}>
            <>{value}</>
          </Tooltip>
        </div>
      );
    },
    filtrable: true,
  },
  { id: 'accessRights', label: 'Access rights' },
  {
    id: 'createdAt',
    label: 'Created',
    sortable: true,
    type: 'date',
    showTime: true,
  },
  {
    id: 'updatedAt',
    label: 'Edited',
    sortable: true,
    type: 'date',
    showTime: 'true',
  },
];

const config = {
  filtered: true,
  pagination: true,
  addButton: true,
  defaultSort: { field: 'updatedAt', dir: 'desc', type: 'date' },
};

interface Props {
  onProjectSelectedCallback?: () => void; 
}

function ProjectList({ onProjectSelectedCallback }: Props) {
  const [globalState] = useContext(GlobalContext);
  const isUserRootContributor = globalState.auth && globalState.auth.isUserRootContributor ? globalState.auth.isUserRootContributor : false;
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPanelOpen, setOpenPanel] = useState(false);
  const [isAddingProject, setIsAddingProject] = useState(false);
  // global store
  const [, globalDispatch] = useContext(GlobalContext);
  const [formattedProjects, setFormattedProjects] = React.useState(data);

  useEffect(() => {
    const _projects = data.map((project: any) => {
      const canWrite = project.canEdit;
      const canRead = project.canRead;
      let accessRight;
      if (canWrite) {
        accessRight = 'Write';
      } else if (!canWrite && canRead) {
        accessRight = 'Read';
      } else {
        accessRight = 'N/A';
      }
      project.accessRights = accessRight;
      project.description = project.description !== null ? project.description : '';
      return project;
    });
    setFormattedProjects(_projects as any);
  }, [data]);

  async function addProject(name: string, description: string) {
    console.log('addProject has been called...');
    try {
      setIsAddingProject(true);
      await Api.addProject(name, description).then(response => {
        console.log('addProject, response: ', response.data);
        // After adding a project we have to fetch all the projects, in order to get
        // the guid for the newly created project. We could instead just use the ID of the
        // response, but that way the new project would be missing the correct dates.
        setIsAddingProject(false);
        setOpenPanel(false);
        fetchProjects();
      });
    } catch (e) {
      setError(e.message || 'Unexpected error');
    }
  }

  async function fetchProjects() {
    try {
      const response = await Api.getProjects();
      const projects = response.data;
      if (Array.isArray(projects)) {
         setData(projects);
      }
      else
      {
        globalDispatch({
          type: ActionType.SET_MESSAGE,
          message: 'Unexpected error: Invalid projects data',
        });
        globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
        globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });        
      }   
    } catch (e) {
      setError(e.message || 'Unexpected error');
    }
    setLoading(false);
  }

  function onNavigateToFolder(selectedProject: Project) {
    if (selectedProject && selectedProject.name) {     
      navigate('/project/' + selectedProject.id);
      onProjectSelectedCallback();
    }
  }

  useEffect(() => {
    fetchProjects();
    // if we are fetching projects, we are on the projects page, so we should reset the current project
    // store.dispatch(setCurrentProject(null));
    globalDispatch({
      type: ActionType.SET_CURRENT_PROJECT_AND_PATH,
      data: { project: null, path: [] },
    });
  }, []);

  if (error) {
    return <div style={{ color: 'red' }}>ERROR: {error}</div>;
  }

  return (
    <>
      <MikeSlidingPanel
        isOpen={isPanelOpen}
        onClose={() => {
          setOpenPanel(false);
        }}
        titleArea={'Create a new project'}
        position="right"
        contentArea={<ProjectCreateForm onAdd={addProject} isAddingProject={isAddingProject} />}
        actionsArea={null}
        noGrayOverlay={false}
      />

      <MikeTable
        loading={loading}
        onSelect={onNavigateToFolder}
        rows={formattedProjects}
        config={config}
        columns={columns}
        maxHeight={'65vh'}
        onAdd={() => {
          setOpenPanel(true);
        }}
        title={'Subfolders'}
        addButtonText={'Create folder'}
        canEditFolder={isUserRootContributor}
      />
    </>
  );
}

export default ProjectList;
